/**
 * Shared API client for analytics-ui. Mirrors the orders-ui client: attaches
 * auth headers, handles 401, and unwraps the StandardApiResponse envelope.
 */

import {
  ApiError,
  getApiHeaders,
  handleUnauthorized,
  type StandardApiResponse,
} from '@lognai/plugin-sdk';
import { userFacingAnalyticsError } from '../lib/user-facing';

export const apiFetch = async <T>(url: string, options: RequestInit = {}): Promise<T> => {
  const response = await fetch(url, {
    ...options,
    headers: { ...getApiHeaders(), ...options.headers },
  });

  if (response.status === 401) {
    handleUnauthorized();
    throw new ApiError('Unauthorized', { type: 'unauthorized' });
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }));
    throw new ApiError(userFacingAnalyticsError(error.error || error.message || `HTTP ${response.status}`), error.details);
  }

  const apiResponse: StandardApiResponse<T> = await response.json();
  if (!apiResponse.success) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    throw new ApiError(userFacingAnalyticsError(apiResponse.error || 'API request failed'), (apiResponse as any).details);
  }
  return apiResponse.data;
};

/** POST helper for the analytics query endpoints. */
export const apiPost = <T>(url: string, body: unknown): Promise<T> =>
  apiFetch<T>(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
